import React, { useEffect, useRef, useState } from 'react';
import { Eraser, PenLine, Check } from 'lucide-react';
import { API, api } from '../../utils/deliveryApi';

// Finger / stylus / mouse signature on a canvas, saved as PNG to the protocol.
const SignaturePad = ({ protocolId, role, label, signature, defaultName = '', onSaved, disabled }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);
  const [name, setName] = useState(signature?.signer_name || defaultName);
  const [editing, setEditing] = useState(!signature);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!editing) return;
    const c = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * ratio;
    c.height = c.offsetHeight * ratio;
    const ctx = c.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.4; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#111';
  }, [editing]);

  const pos = (e) => { const r = canvasRef.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const down = (e) => { e.preventDefault(); canvasRef.current.setPointerCapture(e.pointerId); drawing.current = true; const ctx = canvasRef.current.getContext('2d'); ctx.beginPath(); ctx.moveTo(...pos(e)); };
  const move = (e) => { if (!drawing.current) return; const ctx = canvasRef.current.getContext('2d'); ctx.lineTo(...pos(e)); ctx.stroke(); setEmpty(false); };
  const up = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); setEmpty(true); };

  const save = async () => {
    setBusy(true); setError('');
    try {
      await api(`/${protocolId}/signatures/${role}`, { method: 'POST', body: { signer_name: name, image: canvasRef.current.toDataURL('image/png') } });
      setEditing(false);
      await onSaved();
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  return (
    <div>
      <span className="dlv-label">{label}</span>
      {editing && !disabled ? (
        <>
          <input className="dlv-input" style={{ marginBottom: 8 }} placeholder="Jméno a příjmení" value={name} onChange={(e) => setName(e.target.value)} aria-label={`${label} – jméno`} />
          <canvas ref={canvasRef} className="dlv-sign" aria-label={`${label} – podpisové pole`}
            onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" className="dlv-iconbtn" onClick={clear}><Eraser size={16} /> Smazat</button>
            <button type="button" className="dlv-btn dlv-btn--dark" disabled={empty || !name.trim() || busy} onClick={save}><Check size={16} /> Uložit podpis</button>
          </div>
        </>
      ) : signature ? (
        <>
          <img className="dlv-sign dlv-sign--done" style={{ objectFit: 'contain' }} src={`${API}/${protocolId}/signatures/${role}?v=${encodeURIComponent(signature.signed_at)}`} alt={`${label}: ${signature.signer_name}`} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
            <strong>{signature.signer_name}</strong>
            <span style={{ color: '#6b6b6b', fontSize: 13 }}>{new Date(signature.signed_at).toLocaleString('cs-CZ')}</span>
            {!disabled && <button type="button" className="dlv-iconbtn" style={{ marginLeft: 'auto' }} onClick={() => setEditing(true)}><PenLine size={16} /> Podepsat znovu</button>}
          </div>
        </>
      ) : <p style={{ color: '#6b6b6b' }}>Nepodepsáno</p>}
      {error && <p role="alert" style={{ color: '#b71c1c', fontSize: 13, margin: '6px 0 0' }}>{error}</p>}
    </div>
  );
};

export default SignaturePad;
